import { Reveal } from '@/components/motion';
import { ArrowRight, CheckCircle } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { IMAGES } from '@/data/site';

const roles = [
  { title: 'Community Transport Drivers', location: 'National · Multiple locations', type: 'Full time / Casual',
    body: 'Support participants and community members with dignified door-to-door transport. Driver training, WWCC and disability-awareness training provided.' },
  { title: 'Wheelchair Accessible Vehicle Operators', location: 'VIC / WA — hubs', type: 'Full time',
    body: 'Certified restraint operator training and hydraulic-lift qualifications provided. Ideal for drivers with health, aged care or disability service backgrounds.' },
  { title: 'Care Coordinators', location: 'Melbourne / Perth', type: 'Full time',
    body: 'Coordinate bookings, work with support coordinators, families and hospital discharge teams. Health, disability or logistics background welcomed.' },
  { title: '24/7 Duty Dispatch', location: 'Remote & hub-based', type: 'Full time / Rotating shifts',
    body: 'Own the after-hours dispatch desk. Calm under pressure, comfortable with logistics tools and care-first thinking.' },
];

export default function Careers() {
  return (
    <>
      <section className="pt-16 md:pt-24 pb-14" data-testid="careers-hero">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="overline mb-6">Careers</div>
                <h1 className="h-display text-balance">
                  Work that means something. Every single day.
                </h1>
                <p className="body-lg mt-8 max-w-2xl text-pretty">
                  We hire people who lead with care and are looking for a job where their patience,
                  reliability and empathy get rewarded — not tolerated.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="rounded-3xl overflow-hidden aspect-[4/5] bg-neutral-100">
                  <img src={IMAGES.team_meeting} alt="Team meeting" className="w-full h-full object-cover" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-neutral-50" data-testid="careers-roles">
        <div className="container-x">
          <div className="max-w-2xl mb-14">
            <Reveal>
              <div className="overline mb-5">Open roles</div>
              <h2 className="h-section text-balance">Currently hiring across Australia.</h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {roles.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.05}>
                <div className="p-7 rounded-3xl bg-white border border-neutral-200 hover:border-brand transition h-full">
                  <div className="flex items-center justify-between mb-3">
                    <span className="badge-brand">{r.type}</span>
                    <span className="text-xs text-ink-muted">{r.location}</span>
                  </div>
                  <h3 className="font-display font-bold text-xl text-ink">{r.title}</h3>
                  <p className="body-md mt-3">{r.body}</p>
                  <Link to="/contact?subject=Careers" className="mt-5 inline-flex items-center gap-1.5 text-brand font-semibold text-sm">
                    Apply now <ArrowRight size={14} weight="bold" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container-x max-w-3xl">
          <Reveal>
            <div className="overline mb-5">What you get</div>
            <h2 className="h-section text-balance">Every role at Inclusive Mobility Group comes with the same commitments.</h2>
          </Reveal>
          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              'Above-award pay and paid training days',
              'Genuine career pathways in a growing national provider',
              'Modern, well-maintained vehicles — not "the old fleet"',
              'Rostering that respects your life outside of work',
              'Ongoing disability, dementia and first-aid training',
              'Employee assistance program and mental health support',
            ].map((b, i) => (
              <li key={b} className="flex items-start gap-3 p-4 rounded-2xl bg-neutral-50 border border-neutral-200">
                <CheckCircle size={20} weight="fill" className="text-brand mt-0.5 shrink-0" />
                <span className="text-ink font-medium text-sm">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}
